import React, { useEffect } from 'react';
import { Container, Row, Col, Card, Spinner, Alert, Carousel, Badge, ListGroup } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../store/store';
import { fetchNews, fetchEditorPicks, fetchTopRated, fetchCategories } from '../store/slices/newsSlice';
import { Link } from 'react-router-dom';
import { News, Category } from '../types/api';

interface NewsItem extends Omit<News, 'short_description'> {
  // Make short_description optional to match the API response
  short_description?: string;
}

interface CategoryItem extends Category {
  // Extending the Category type from the API
}

interface NewsResponse {
  results: NewsItem[];
  count: number;
  next: string | null;
  previous: string | null;
}
// Simple date formatter
const formatDate = (dateString: string) => {
  const options: Intl.DateTimeFormatOptions = { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  };
  return new Date(dateString).toLocaleDateString('ru-RU', options);
};

const HomePage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { 
    items: newsItems = [],
    editorPicks = [],
    topRated = [],
    categories = [],
    status, 
    error 
  } = useAppSelector((state) => state.news);

  // Handle case when newsItems is not loaded yet
  useEffect(() => {
    if (status === 'idle') {
      dispatch(fetchNews({ page: 1, page_size: 6 }));
      dispatch(fetchEditorPicks());
      dispatch(fetchTopRated());
      dispatch(fetchCategories());
    }
  }, [dispatch, status]);



  if (status === 'loading') {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <Alert variant="danger" className="m-3">
        <Alert.Heading>Ошибка загрузки данных</Alert.Heading>
        <p>{error || 'Неизвестная ошибка'}</p>
      </Alert>
    );
  }

  // Handle case when newsItems is not loaded yet
  if (!newsItems || newsItems.length === 0) {
    return (
      <Container className="py-5">
        <Alert variant="info">Новостей пока нет</Alert>
      </Container>
    );
  }

  return (
    <div className="home-page">
      {/* Шапка */}
      <header className="py-3 border-bottom">
        <Container>
          <Row className="align-items-center">
            <Col xs={12} md={4} className="text-center text-md-start mb-3 mb-md-0">
              <h1 className="h3 mb-0">
                <Link to="/" className="text-decoration-none text-dark">
                  AutoFill
                </Link>
              </h1>
            </Col>
            <Col xs={12} md={8}>
              <nav className="d-flex justify-content-center justify-content-md-end">
                <Link to="/" className="me-3 text-decoration-none">Главная</Link>
                <Link to="/news" className="me-3 text-decoration-none">Новости</Link>
                <Link to="/categories" className="me-3 text-decoration-none">Категории</Link>
                <Link to="/about" className="text-decoration-none">О нас</Link>
              </nav>
            </Col>
          </Row>
        </Container>
      </header>

      {/* Основной контент */}
      <main className="py-4">
        <Container>
          <Row>
            {/* Левая колонка - Категории */}
            <Col lg={3} className="mb-4">
              <Card>
                <Card.Header className="bg-primary text-white">
                  Категории
                </Card.Header>
                <ListGroup variant="flush">
                  {categories.map((category) => (
                    <ListGroup.Item key={category.id} action as={Link} to={`/category/${category.slug}`}>
                      {category.name}
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Card>
            </Col>

            {/* Центральная колонка - Основной контент */}
            <Col lg={6} className="mb-4">
              {/* Выбор редакции */}
              {editorPicks && editorPicks.length > 0 && (
                <Card className="mb-4">
                  <Card.Header className="bg-light">
                    <h2 className="h5 mb-0">Выбор редакции</h2>
                  </Card.Header>
                  <Card.Body>
                    <Carousel>
                      {editorPicks.map((item: NewsItem) => (
                        <Carousel.Item key={item.id}>
                          <Card.Img variant="top" src={item.image_url || '/images/placeholder.jpg'} />
                          <Card.Body>
                            <Card.Title>{item.title}</Card.Title>
                            <Card.Text>{item.short_description}</Card.Text>
                            <Link to={`/news/${item.id}`} className="btn btn-primary">
                              Читать далее
                            </Link>
                          </Card.Body>
                        </Carousel.Item>
                      ))}
                    </Carousel>
                  </Card.Body>
                </Card>
              )}

              {/* Свежие новости */}
              <Card>
                <Card.Header className="bg-light">
                  <h2 className="h5 mb-0">Свежие новости</h2>
                </Card.Header>
                <ListGroup variant="flush">
                  {topRated.slice(0, 3).map((item: NewsItem) => (
                    <ListGroup.Item key={item.id} className="py-3">
                      <div className="d-flex">
                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <span className="badge bg-primary">{item.category?.name}</span>
                            <small className="text-muted">{formatDate(item.created_at)}</small>
                          </div>
                          <h3 className="h5 mb-1">
                            <Link to={`/news/${item.id}`} className="text-decoration-none">
                              {item.title}
                            </Link>
                          </h3>
                          <p className="mb-0 text-muted">{item.short_description}</p>
                        </div>
                        {item.image_url && (
                          <div className="ms-3" style={{ width: '100px', flexShrink: 0 }}>
                            <img 
                              src={item.image_url} 
                              alt={item.title}
                              className="img-fluid rounded"
                              style={{ width: '100%', height: '70px', objectFit: 'cover' }}
                            />
                          </div>
                        )}
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Card>
            </Col>

            {/* Правая колонка - Сайдбар */}
            <Col lg={3}>
              {/* Популярное */}
              <Card className="mb-4">
                <Card.Header className="bg-light">
                  <h2 className="h5 mb-0">Популярное</h2>
                </Card.Header>
                <ListGroup variant="flush">
                  {newsItems && newsItems.length > 0 ? (
                    newsItems.map((item: NewsItem) => (
                      <ListGroup.Item key={item.id} action as={Link} to={`/news/${item.id}`}>
                        {item.title}
                      </ListGroup.Item>
                    ))
                  ) : (
                    <ListGroup.Item>Нет популярных новостей</ListGroup.Item>
                  )}
                </ListGroup>
              </Card>

              {/* Топ новостей */}
              <Card>
                <Card.Header className="bg-light">
                  <h2 className="h5 mb-0">Топ новостей</h2>
                </Card.Header>
                <ListGroup variant="flush">
                  {newsItems.slice(0, 5).map((item, index) => (
                    <ListGroup.Item key={item.id} action as={Link} to={`/news/${item.id}`}>
                      <div className="d-flex align-items-center">
                        <span className="badge bg-primary me-2">{index + 1}</span>
                        <span className="text-truncate">{item.title}</span>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Card>
            </Col>
          </Row>
        </Container>
      </main>

      {/* Подвал */}
      <footer className="py-4 bg-light mt-5">
        <Container>
          <Row>
            <Col md={6} className="text-center text-md-start">
              <p className="mb-0">© 2025 AutoFill. Все права защищены.</p>
            </Col>
            <Col md={6} className="text-center text-md-end">
              <Link to="/about" className="text-decoration-none me-3">О нас</Link>
              <Link to="/contacts" className="text-decoration-none me-3">Контакты</Link>
              <Link to="/privacy" className="text-decoration-none">Политика конфиденциальности</Link>
            </Col>
          </Row>
        </Container>
      </footer>
    </div>
  );
};

export default HomePage;
